import { useState, useEffect, useRef } from 'react';
import { FiActivity, FiTrash2, FiPause, FiPlay } from 'react-icons/fi';
import useStore from '../store/useStore';
import './MIDIMonitor.css';

/**
 * MIDI Monitor Component
 * Real-time display of MIDI messages for debugging
 */

function MIDIMonitor() {
    // Selector subscriptions: this component only cares about the message list
    // and connection state — not the rest of the store.
    const midiMessages = useStore(state => state.midiMessages);
    const isConnected = useStore(state => state.isConnected);
    const clearMIDIMessages = useStore(state => state.clearMIDIMessages);
    const [isPaused, setIsPaused] = useState(false);
    const [filter, setFilter] = useState('all'); // all, noteOn, noteOff, cc, pc
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        if (!isPaused) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        scrollToBottom();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [midiMessages]);

    const clearMessages = () => {
        clearMIDIMessages();
    };

    const getFilteredMessages = () => {
        if (filter === 'all') return midiMessages;
        return midiMessages.filter(msg => {
            if (filter === 'noteOn' && msg.type === 'noteon') return true;
            if (filter === 'noteOff' && msg.type === 'noteoff') return true;
            if (filter === 'cc' && msg.type === 'controlchange') return true;
            if (filter === 'pc' && msg.type === 'programchange') return true;
            return false;
        });
    };

    const getMessageIcon = (type) => {
        switch (type) {
            case 'noteon': return '🎵';
            case 'noteoff': return '🔇';
            case 'controlchange': return '🎛️';
            case 'programchange': return '💾';
            default: return '📡';
        }
    };

    const getMessageColor = (type) => {
        switch (type) {
            case 'noteon': return 'note-on';
            case 'noteoff': return 'note-off';
            case 'controlchange': return 'cc';
            case 'programchange': return 'pc';
            default: return 'other';
        }
    };

    const filteredMessages = getFilteredMessages();

    return (
        <div className="midi-monitor glass">
            <div className="monitor-header">
                <div className="monitor-title">
                    <FiActivity />
                    <span>MIDI Monitor</span>
                    {isConnected && (
                        <span className="live-indicator">● LIVE</span>
                    )}
                </div>
                <div className="monitor-controls">
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="filter-select"
                        aria-label="Filter MIDI message types"
                    >
                        <option value="all">All Messages</option>
                        <option value="noteOn">Note On</option>
                        <option value="noteOff">Note Off</option>
                        <option value="cc">Control Change</option>
                        <option value="pc">Program Change</option>
                    </select>
                    <button
                        className="btn-icon"
                        onClick={() => setIsPaused(!isPaused)}
                        title={isPaused ? 'Resume' : 'Pause'}
                    >
                        {isPaused ? <FiPlay /> : <FiPause />}
                    </button>
                    <button
                        className="btn-icon"
                        onClick={clearMessages}
                        title="Clear messages"
                    >
                        <FiTrash2 />
                    </button>
                </div>
            </div>

            <div className="monitor-messages">
                {filteredMessages.length === 0 ? (
                    <div className="empty-state">
                        <FiActivity size={48} />
                        <p>No MIDI messages yet</p>
                        <p className="text-sm text-secondary">
                            {!isConnected ? 'Connect to MIDI first' : 'Messages will appear here'}
                        </p>
                    </div>
                ) : (
                    <>
                        {filteredMessages.map(msg => (
                            <div key={msg.id} className={`midi-message ${getMessageColor(msg.type)} ${msg.direction === 'out' ? 'out' : 'in'}`}>
                                <span className="message-icon">
                                    {msg.direction === 'out' ? '📤' : getMessageIcon(msg.type)}
                                </span>
                                <div className="message-details">
                                    <span className="message-type">{msg.type.toUpperCase()}</span>
                                    {msg.note !== undefined && (
                                        <span className="message-data">Note: {msg.note}</span>
                                    )}
                                    {msg.value !== undefined && (
                                        <span className="message-data">Value: {msg.value}</span>
                                    )}
                                    {msg.velocity !== undefined && (
                                        <span className="message-data">Vel: {msg.velocity}</span>
                                    )}
                                    {msg.controller !== undefined && (
                                        <span className="message-data">CC: {msg.controller}</span>
                                    )}
                                    {msg.program !== undefined && (
                                        <span className="message-data">Program: {msg.program}</span>
                                    )}
                                    <span className="message-channel">Ch{msg.channel || 1}</span>
                                </div>
                                {msg.direction === 'out' && (
                                    <span className="sent-badge" title="Message sent successfully">✅ Sent</span>
                                )}
                                <span className="message-time">
                                    {new Date(msg.timestamp).toLocaleTimeString('en-US', {
                                        hour12: false,
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                        fractionalSecondDigits: 3
                                    })}
                                </span>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </div>

            <div className="monitor-footer">
                <span className="text-sm text-secondary">
                    {filteredMessages.length} message{filteredMessages.length !== 1 ? 's' : ''}
                    {filter !== 'all' && ` (${filter} filtered)`}
                </span>
            </div>
        </div>
    );
}

export default MIDIMonitor;
